'use client';

import { Swap } from '@drift-labs/icons';
import { BigNum, SpotMarketConfig } from '@drift-labs/sdk';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { MIN_LEFTOVER_SOL, STABLE_COIN_SYMBOLS } from 'src/constants/constants';
import Env from 'src/environmentVariables/EnvironmentVariables';
import { useSpotMarketsSortedByMarketIndex } from 'src/hooks/spotMarkets/useSpotMarketsSortedByMarketIndex';
import useSwapFormEngine from 'src/hooks/swap/useSwapFormEngine';
import useSwapMarkets from 'src/hooks/swap/useSwapMarkets';
import {
	useJupiterTokenPairData,
	useTokenPairData,
} from 'src/hooks/swap/useTokenPairData';
import useUpdateSwapParams from 'src/hooks/swap/useUpdateSwapParams';
import { cj } from 'src/utils/classNames';
import useCurrentSettings from '../../hooks/useCurrentSettings';
import Checkbox from '../primitives/Checkbox/Checkbox';
import useDriftTheme from 'src/hooks/useDriftTheme';
import { DriftTheme } from 'src/environmentVariables/EnvironmentVariables';
import DefinitionTooltip from 'src/components/Tooltip/DefinitionTooltip/DefinitionTooltip';
import Switch from '../primitives/Switch/Switch';
import { MAX_SPOT_POSITIONS_ERROR_MESSAGE } from 'src/constants/markets';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import SwapQuoteErrorDisplay from './SwapQuoteErrorDisplay';
import {
	MAIN_POOL_ID,
	UIMarket,
	truncateInputToPrecision,
} from '@drift/common';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import handleSpecialRendering from 'src/utils/handleSpecialRendering';
import { isAmplifyAccountName } from 'src/utils/amplify';
import { useTickedAccountData } from 'src/hooks/useAccountData';
import { useSwapPairWarning } from 'src/hooks/swap/useSwapPairWarning';
import useTxDataStore from 'src/stores/useTxDataStore';
import DisabledOverlayMessage from 'src/components/Utils/DisabledOverlayMessage';
import { DEFAULT_SWAP_SLIPPAGE_TOLERANCE_PCT } from 'src/constants/swap';
import CollapsibleSlippageToleranceInput from 'src/components/TradeForm/SlippageToleranceInput/CollapsibleSlippageToleranceInput';
import IconButton from 'src/components/primitives/IconButton/IconButton';
import SwapFormInputs from 'src/components/Swap/SwapFormInputs';
import Button from 'src/components/primitives/Button/Button';
import Slider from 'src/components/primitives/Slider/Slider';
import { UserSettings } from 'src/environmentVariables/EnvironmentVariables';
import Select from 'src/components/primitives/Select/Select';
import useSelect from 'src/components/primitives/Select/hooks/useSelect';
import TitanBadge from './TitanBadge';
import { useSwapClient } from 'src/hooks/driftActions/swaps/useSwapClient';
import InlineAlert from 'src/components/primitives/InlineAlert/InlineAlert';
import { SelectOption } from 'src/components/primitives/Select/hooks/types';
import MarginCheckbox from 'src/components/TradeForm/MarginCheckbox';

const SwapForm = () => {
	const spotMarketsSortedByMarketIndex = useSpotMarketsSortedByMarketIndex();
	const updateSwapParams = useUpdateSwapParams();
	const solBalance = useDriftStore((s) => s.wallet.currentSolBalance);
	const { swapFromMarket, swapToMarket } = useSwapMarkets();
	const { fromTokenPrice, toTokenPrice } = useTokenPairData(
		swapFromMarket?.symbol,
		swapToMarket?.symbol
	);

	const tokenPairPrice = useJupiterTokenPairData(swapFromMarket, swapToMarket);
	const [savedSettings] = useCurrentSettings();
	const directRouteSwaps = savedSettings.directRouteSwaps;
	const [savedSettingsState, setSavedSettingsState] = useCurrentSettings();
	const { updateSwapClient } = useSwapClient();

	const handleSwapClientChange = (value: UserSettings['swapClientType']) => {
		setSavedSettingsState({
			...savedSettingsState,
			swapClientType: value,
		});

		setTimeout(() => {
			updateSwapClient();

			if (swapFromAmount) {
				setSwapFromAmount(swapFromAmount, true);
			} else if (swapToAmount) {
				setSwapToAmount(swapToAmount);
			}
		}, 0);
	};

	const [hasAgreedToPriceDivergence, setHasAgreedToPriceDivergence] =
		useState(false);

	const poolId = useDriftAccountStore(
		(s) =>
			s.accounts[s.currentUserKey ?? '']?.client?.getUserAccount()?.poolId ||
			MAIN_POOL_ID
	);

	const {
		enableVersionedTx,
		enableVersionedTransaction,
		swapMode,
		setSwapMode,
		swapFromAmount,
		setSwapFromAmount,
		swapToAmount,
		setSwapToAmount,
		inputNumericAmount,
		quoteDetails,
		swapPrice,
		resetForm,
		handleSwap,
		isLoading,
		setIsLoading,
		isButtonDisabled,
		fromTokenBalance,
		maxSwapSize,
		isInsufficientBalance,
		isGeoblocked,
		borrowAmount,
		isApprovingTxn,
		hasMaxSpotBalance,
		showPriceEstimateOracleDivergenceWarning,
		priceDivergencePct,
		isAboveMaxSwapSizeWithoutLeverage,
		isAccountMarginEnabled,
		isMarginCheckboxChecked,
		toggleIsMarginCheckboxChecked,
	} = useSwapFormEngine(
		swapFromMarket,
		swapToMarket,
		DEFAULT_SWAP_SLIPPAGE_TOLERANCE_PCT
	);

	const txErrors = useTxDataStore((s) => s.txErrors);
	const swapTxErrors = useMemo(() => {
		return txErrors.filter((txError) => txError.type === 'swap');
	}, [txErrors]);

	const swapPairWarning = useSwapPairWarning({
		fromToken: swapFromMarket,
		toToken: swapToMarket,
	});

	const hasEnoughSolInWallet = solBalance.gte(MIN_LEFTOVER_SOL);

	const currentUserName = useTickedAccountData()?.name ?? '';
	const isAmplifyAccount = isAmplifyAccountName(currentUserName);
	const currentUserKey = useDriftAccountStore((s) => s.currentUserKey);

	// Get current theme for logo selection
	const driftTheme = useDriftTheme();
	const isDarkTheme = driftTheme === DriftTheme.dark;

	const swapClientOptions: SelectOption<UserSettings['swapClientType']>[] =
		useMemo(
			() => [
				{
					value: 'titan',
					label: (
						<div className="flex items-center gap-x-sm">
							<span>Titan</span>
							<TitanBadge />
						</div>
					),
				},
				{
					value: 'jupiter',
					label: 'Jupiter',
				},
			],
			[]
		);

	const {
		value: swapClientValue,
		open: swapClientOpen,
		onOpenChange: onSwapClientOpenChange,
		onValueChange: onSwapClientValueChange,
		selectOptions: swapClientSelectOptions,
		selectedOption: swapClientSelectedOption,
	} = useSelect({
		selectedValue: savedSettingsState.swapClientType,
		onSelectedValueChange: handleSwapClientChange,
		options: swapClientOptions,
	});

	const selectedLabelBySwapClient: Record<
		UserSettings['swapClientType'],
		ReactNode
	> = {
		titan: (
			<img
				className="h-4"
				src={
					isDarkTheme
						? '/assets/images/swap/titan-swap-logo-dark.svg'
						: '/assets/images/swap/titan-swap-logo-light.svg'
				}
			/>
		),
		jupiter: (
			<img
				className="h-4"
				src={
					isDarkTheme
						? '/assets/images/swap/jupiter-swap-logo-dark.svg'
						: '/assets/images/swap/jupiter-swap-logo-light.svg'
				}
			/>
		),
	};

	const selectedLabel = swapClientSelectedOption
		? selectedLabelBySwapClient[swapClientSelectedOption.value]
		: 'Select...';

	const handleFromMarketChange = (market: SpotMarketConfig) => {
		if (swapToMarket.symbol === market.symbol) {
			reverseTokenPair();
		} else {
			updateSwapParams(market.symbol, swapToMarket.symbol, poolId);
		}
	};

	const handleToMarketChange = (market: SpotMarketConfig) => {
		if (swapFromMarket.symbol === market.symbol) {
			reverseTokenPair();
		} else {
			updateSwapParams(swapFromMarket.symbol, market.symbol, poolId);
		}
	};

	const reverseTokenPair = () => {
		if (isLoading) return;
		const _swapToAmount = swapToAmount;
		const _swapFromAmount = swapFromAmount;
		resetForm();
		updateSwapParams(swapToMarket.symbol, swapFromMarket.symbol, poolId);
		setTimeout(() => {
			setIsLoading(true);
			if (swapMode === 'ExactOut') {
				setSwapMode('ExactIn');
				setSwapFromAmount(_swapToAmount);
			} else {
				setSwapMode('ExactOut');
				setSwapToAmount(_swapFromAmount);
			}
		}, 1);
	};

	const handleSwapToAmountUserChange = (val: string | undefined) => {
		if (val === undefined) {
			return;
		}

		setSwapMode('ExactOut');
		const truncatedInput = truncateInputToPrecision(
			val,
			swapToMarket.precisionExp
		);
		setSwapToAmount(truncatedInput);
	};

	const handleSwapFromAmountUserChange = (
		val: string | undefined,
		callRouteFetch = true
	) => {
		if (val === undefined) {
			return;
		}

		setSwapMode('ExactIn');
		const truncatedInput = truncateInputToPrecision(
			val,
			swapFromMarket.precisionExp
		);
		setSwapFromAmount(truncatedInput, callRouteFetch);
	};

	const handleTryExactIn = () => {
		if (swapToAmount && tokenPairPrice) {
			const estimatedSwapFromAmount =
				(Number(swapToAmount) / tokenPairPrice) * 1.001;

			handleSwapFromAmountUserChange(`${estimatedSwapFromAmount}`);
		}
	};

	useEffect(() => {
		if (swapFromMarket.poolId !== poolId || swapToMarket.poolId !== poolId) {
			const markets = spotMarketsSortedByMarketIndex.filter(
				(market) => market.poolId === poolId
			);
			if (markets.length > 1) {
				updateSwapParams(markets[0].symbol, markets[1].symbol, poolId);
			}
		}
	}, [poolId, swapFromMarket, swapToMarket]);

	const swapFromMarketUIConfig = UIMarket.createSpotMarket(
		swapFromMarket.marketIndex
	);
	const swapToMarketUIConfig = UIMarket.createSpotMarket(
		swapToMarket.marketIndex
	);

	// no swap liquidity for these tokens
	const showBlurOverlay = poolId === 3 || poolId === 4;

	const priceImpact = +(quoteDetails?.priceImpactPct ?? '0');
	const priceImpactString = (() => {
		if (!quoteDetails) {
			return '-';
		}

		if (!priceImpact) {
			return '0';
		}

		if (priceImpact > 0.001) {
			return `${(priceImpact * 100).toFixed(2)}%`;
		}

		return '< 0.1%';
	})();

	const submitButtonText = (() => {
		if (quoteDetails?.error) {
			return 'No routes found';
		}

		if (!hasEnoughSolInWallet) {
			return 'Not Enough SOL In Wallet';
		}

		if (isInsufficientBalance) {
			return 'Insufficient Balance';
		}

		return 'Swap';
	})();

	return (
		<div className="relative flex size-full flex-col gap-y-lg border-0 bg-container-bg p-md sm:rounded-sm">
			{showBlurOverlay && (
				<DisabledOverlayMessage
					title="Swapping is not available at this time for tokens in this pool."
					message="Swapping is not available at this time for tokens in this pool."
				/>
			)}

			<div className="flex items-center justify-between">
				<span className="typo-b4 text-text-secondary">Swap Client</span>
				<Select.Root
					open={swapClientOpen}
					onOpenChange={onSwapClientOpenChange}
					value={swapClientValue}
					onValueChange={onSwapClientValueChange}
				>
					<Select.Trigger className="border-none bg-transparent">
						{selectedLabel}
					</Select.Trigger>
					<Select.Options className="w-[276px]">
						{swapClientSelectOptions.map((option) => (
							<Select.Option key={option.value} value={option.value}>
								{option.label}
							</Select.Option>
						))}
					</Select.Options>
				</Select.Root>
			</div>

			<SwapFormInputs
				swapFromAmount={swapFromAmount}
				handleSwapFromAmountUserChange={handleSwapFromAmountUserChange}
				swapToAmount={swapToAmount}
				handleSwapToAmountUserChange={handleSwapToAmountUserChange}
				swapFromMarket={swapFromMarket}
				handleFromMarketChange={handleFromMarketChange}
				swapToMarket={swapToMarket}
				handleToMarketChange={handleToMarketChange}
				fromTokenBalance={fromTokenBalance}
				maxSwapSize={maxSwapSize}
				fromTokenPrice={fromTokenPrice}
				toTokenPrice={toTokenPrice}
				reverseTokenPair={reverseTokenPair}
				currentUserKey={currentUserKey}
				poolId={poolId}
			/>

			{isAccountMarginEnabled && isMarginCheckboxChecked && (
				<LeverageSlider
					swapFromNumericAmount={inputNumericAmount}
					maxSwapSize={maxSwapSize}
					setSwapAmount={handleSwapFromAmountUserChange}
				/>
			)}

			<MarginCheckbox
				isAccountMarginEnabled={isAccountMarginEnabled}
				checked={isMarginCheckboxChecked}
				onCheckedChange={toggleIsMarginCheckboxChecked}
			/>

			<div className="py-sm">
				<Button
					size="lg"
					className="w-full"
					variant="positive"
					onClick={handleSwap}
					disabled={
						isButtonDisabled ||
						!hasEnoughSolInWallet ||
						(showPriceEstimateOracleDivergenceWarning &&
							!hasAgreedToPriceDivergence)
					}
					isLoading={isApprovingTxn}
				>
					{submitButtonText}
				</Button>
			</div>

			{isAmplifyAccount && (
				<InlineAlert
					variant="warning"
					title="You are altering your Amplify position."
				>
					Swapping will alter the leverage and net value of your Amplify
					position and may cause issues. Please use the Amplify page unless you
					are doing this intentionally.
				</InlineAlert>
			)}

			{showPriceEstimateOracleDivergenceWarning && (
				<InlineAlert variant="warning" title="Price Estimate Oracle Divergence">
					<span>
						Est. swap price is{' '}
						{priceDivergencePct ?? `>${Env.priceDivergenceWarningThreshold}`}%
						from oracle price. Proceeding with this order may result in a poor
						swap price.
					</span>
					<Checkbox.Default
						id="swap-form-price-divergence-warning"
						label="I understand the risk"
						labelClassName="text-text-default"
						checked={hasAgreedToPriceDivergence}
						onCheckedChange={() => {
							setHasAgreedToPriceDivergence((prev) => !prev);
						}}
					/>
				</InlineAlert>
			)}

			{isGeoblocked && (
				<InlineAlert variant="error" title="Restricted Territory">
					You are not allowed to use Drift from a restricted territory.
				</InlineAlert>
			)}

			{isAboveMaxSwapSizeWithoutLeverage && (
				<InlineAlert variant="warning" title="Max Swap Size Without Leverage">
					You have exceeded the max swap size without leverage. If you would
					like to increase the max swap size, you can enable leverage in the
					checkbox above.
				</InlineAlert>
			)}

			{!enableVersionedTx && (
				<InlineAlert variant="warning" title="Enable Versioned Transactions">
					<div>You need to enable Versioned Transactions to use Swaps.</div>
					<Button
						size="sm"
						variant="secondary"
						className="w-fit"
						onClick={enableVersionedTransaction}
					>
						Enable Versioned Transactions
					</Button>
				</InlineAlert>
			)}

			{hasMaxSpotBalance && (
				<InlineAlert
					variant="error"
					title={MAX_SPOT_POSITIONS_ERROR_MESSAGE.title}
				>
					{MAX_SPOT_POSITIONS_ERROR_MESSAGE.message}
				</InlineAlert>
			)}

			{swapPairWarning && swapTxErrors.length > 0 && (
				<InlineAlert variant="info" title="Swap To SOL First">
					{swapPairWarning}
				</InlineAlert>
			)}

			{quoteDetails?.error && !isLoading && (
				<SwapQuoteErrorDisplay
					quoteDetails={quoteDetails}
					swapMode={swapMode}
					useDirectRoute={directRouteSwaps}
					onTryExactIn={handleTryExactIn}
				/>
			)}

			<div className="flex flex-col gap-y-sm">
				<CollapsibleSlippageToleranceInput
					slippageTolerance={DEFAULT_SWAP_SLIPPAGE_TOLERANCE_PCT}
					hideDynamic
				/>

				<SwapDetailsRow
					label="Rate"
					value={
						<SwapPrice
							inAmount={BigNum.fromPrint(
								swapFromAmount,
								swapFromMarket.precisionExp
							)}
							outAmount={BigNum.fromPrint(
								swapToAmount,
								swapToMarket.precisionExp
							)}
							inMarket={swapFromMarketUIConfig}
							outMarket={swapToMarketUIConfig}
							swapPrice={swapPrice}
							isLoading={isLoading}
						/>
					}
				/>

				<SwapDetailsRow
					label="Price Impact"
					value={priceImpactString}
					warn={priceImpact > 0.01}
				/>

				<SwapDetailsRow
					label="Borrow Amount"
					value={
						borrowAmount?.gtZero()
							? `${borrowAmount.print()} ${
									swapFromMarketUIConfig.uiSymbols.marketDisplaySymbol
								}`
							: '-'
					}
				/>

				<DirectRouteSwitch />
			</div>
		</div>
	);
};

interface LeverageSliderProps {
	maxSwapSize: BigNum;
	swapFromNumericAmount: number;
	setSwapAmount: (amount: string, callRouteFetch?: boolean) => void;
}

const LeverageSlider = ({
	swapFromNumericAmount,
	maxSwapSize,
	setSwapAmount,
}: LeverageSliderProps) => {
	const value =
		Math.abs((swapFromNumericAmount / maxSwapSize.toNum()) * 100) || 0;

	const handleSliderValChange = (pct: number, callRouteFetch = true) => {
		if (pct === 100) {
			setSwapAmount(maxSwapSize.toNum().toString(), callRouteFetch);
			return;
		}

		const amount = (maxSwapSize.toNum() * pct) / 100;
		setSwapAmount(amount.toFixed(2), callRouteFetch);
	};

	return (
		<Slider
			value={value}
			onValueChange={handleSliderValChange}
			onValueCommit={handleSliderValChange}
		/>
	);
};

const SwapDetailsRow = ({
	label,
	value,
	warn,
}: {
	label: string;
	value: ReactNode;
	warn?: boolean;
}) => {
	return (
		<div className="flex justify-between typo-b4">
			<span className="text-text-secondary">{label}</span>
			<span className={warn ? 'text-star-yellow' : 'text-text-default'}>
				{value}
			</span>
		</div>
	);
};

const DIRECT_ROUTE_TOOLTIP_TRIGGER_CLASSNAME =
	'flex items-center gap-x-xs text-text-secondary';

function DirectRouteSwitch() {
	const [savedSettings, setSavedSettings] = useCurrentSettings();
	const directRouteSwaps = savedSettings.directRouteSwaps;

	return (
		<div className="flex items-center justify-between">
			<DefinitionTooltip
				definitionId="tradingDirectRouteSwap"
				className={cj('typo-b4', DIRECT_ROUTE_TOOLTIP_TRIGGER_CLASSNAME)}
			>
				Use direct route
			</DefinitionTooltip>
			<Switch
				size="sm"
				checked={directRouteSwaps}
				onCheckedChange={() =>
					setSavedSettings({
						...savedSettings,
						directRouteSwaps: !directRouteSwaps,
					})
				}
			/>
		</div>
	);
}

export const SwapPrice = ({
	inAmount,
	outAmount,
	inMarket,
	outMarket,
	swapPrice,
}: {
	inAmount: BigNum;
	outAmount: BigNum;
	inMarket: UIMarket;
	outMarket: UIMarket;
	isLoading?: boolean;
	swapPrice: number;
}) => {
	const [isSellPrice, setIsSellPrice] = useState(true);

	const inMarketConfig = inMarket.market as SpotMarketConfig;
	const outMarketConfig = outMarket.market as SpotMarketConfig;

	// default to buy price when selling stablecoins
	useEffect(() => {
		if (STABLE_COIN_SYMBOLS.includes(inMarketConfig.symbol)) {
			setIsSellPrice(false);
		} else {
			setIsSellPrice(true);
		}
	}, [inMarketConfig.symbol, outMarketConfig.symbol]);

	const rightSidePrice = isSellPrice ? swapPrice : 1 / swapPrice;

	if (
		(outAmount.eqZero() && inAmount.eqZero()) ||
		rightSidePrice === Infinity ||
		rightSidePrice === 0
	) {
		return '-';
	}

	const precision = isSellPrice
		? outMarket.precisionExp.toNumber()
		: inMarket.precisionExp.toNumber();

	return (
		<div className="flex items-center gap-x-xs text-text-default">
			<span className="typo-b4">
				1{' '}
				{isSellPrice
					? inMarket.uiSymbols.baseAssetDisplaySymbol
					: outMarket.uiSymbols.baseAssetDisplaySymbol}{' '}
				≈{' '}
				{handleSpecialRendering(rightSidePrice.toString(), {
					fallback: rightSidePrice.toLocaleString(Env.locale, {
						maximumFractionDigits: precision,
						minimumFractionDigits: precision,
					}),
				})}{' '}
				{isSellPrice
					? outMarket.uiSymbols.baseAssetSymbol
					: inMarket.uiSymbols.baseAssetSymbol}
			</span>
			<IconButton
				size="sm"
				variant="tertiary"
				onClick={() => setIsSellPrice(!isSellPrice)}
				icon={Swap}
			/>
		</div>
	);
};

export default SwapForm;
